// @flow
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import * as sidePanelActionCreators from 'esm-web-pro/redux/side-panel/actions';
import { ApiFormWithModel } from 'esm-web-pro/containers/forms/FormWithModel';
import { isEmpty } from 'lodash';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import { ApiClient } from 'speed-js-core';
import type { UUID } from 'esm-web-pro/types/common';
import { IntlShape, injectIntl } from 'react-intl';
import type { Pipe } from './Pipe';


type Props = {
    pipe: Pipe,
    onClose: Function,
    onSave: Function,
    mapUuid: string,
    getInfrastructureProvider: Function,
    getProjectStages: (uuid: UUID) => Promise<Object>,
    intl: IntlShape,
};


const PipeEditor = (props: Props) => {
    const {
        pipe, mapUuid, onClose, onSave, getInfrastructureProvider, intl, getProjectStages,
    } = props;
    const { continuous_pipe: continuousPipe, gis_line: gisLine } = pipe;
    const { infrastructure_provider: gisLineInfrastructureProvider } = gisLine;

    const initialExcludeFields = ['path', 'count', 'is_virtual', 'stage'];
    if (continuousPipe) {
        initialExcludeFields.push('infrastructure_provider', 'property_owner');
    }

    if (!pipe.project_stage?.uuid) {
        initialExcludeFields.push('project_stage');
    }

    const formRef = React.useRef(null);
    const [excludeFields, setExcludeFields] = React.useState(initialExcludeFields);
    const [initial, setInitial] = React.useState({
        ...pipe,
        infrastructure_provider: pipe.infrastructure_provider.uuid,
        property_owner: pipe.property_owner?.uuid,
        continuous_pipe: pipe.continuous_pipe?.id || null,
        project: pipe.project?.uuid,
        project_stage: pipe.project_stage?.uuid,
    });
    const [provider, setProvider] = React.useState(pipe.infrastructure_provider);
    const [extraSchema, setExtraSchema] = React.useState([]);
    const [lastSelectedProject, setLastSelectedProject] = React.useState(null);

    const getStageChoices = (project: UUID) => getProjectStages(project).then((res) => {
        const projectStageChoices = {};
        res.results.forEach((stage) => {
            projectStageChoices[stage.uuid] = stage.name;
        });
        return projectStageChoices;
    });

    const removeProjectStageFromExcludedFields = () => {
        const newExcludedFields = excludeFields.filter((field) => field !== 'project_stage');
        setExcludeFields(newExcludedFields);
    };

    React.useEffect(() => {
        if (provider.id !== gisLineInfrastructureProvider) {
            const newExtraSchema = [];

            if (provider.entity.name === process.env.REACT_APP_DEFAULT_PROVIDER) {
                newExtraSchema.push({
                    'fields.export.ui.disabled': true,
                });
            }
            setExtraSchema(newExtraSchema);
        } else if (provider.entity.name === process.env.REACT_APP_DEFAULT_PROVIDER) {
            if (formRef.current) {
                formRef.current.getFormikBag().setFieldValue('provider');
            }
        }
    }, [provider]);

    React.useEffect(() => {
        const newExcludeFields = [];
        if (pipe.infrastructure_provider?.entity?.name !== process.env.REACT_APP_DEFAULT_PROVIDER) {
            newExcludeFields.push('export');
        }

        if (!pipe.infrastructure_provider?.is_property_owner) {
            newExcludeFields.push('property_owner');
        }

        if (!isEmpty(newExcludeFields)) {
            setExcludeFields((e) => [...e, ...newExcludeFields]);
        }
    }, [
        pipe.infrastructure_provider?.entity?.name,
        pipe.continuous_pipe,
        pipe.infrastructure_provider?.is_property_owner,
    ]);

    React.useEffect(() => {
        const newExcludeFields = [...initialExcludeFields];
        const newInitial = {};
        if (provider?.entity?.name !== process.env.REACT_APP_DEFAULT_PROVIDER) {
            newExcludeFields.push('export');
            newInitial.export = false;
        } else {
            newInitial.export = pipe.export;
        }

        if (!provider?.is_property_owner) {
            newExcludeFields.push('property_owner');
            newInitial.property_owner = null;
        } else {
            newInitial.property_owner = pipe.property_owner?.uuid;
        }

        setExcludeFields(newExcludeFields);
        setInitial({ ...initial, ...newInitial });

        if (!isEmpty(newInitial) && formRef.current) {
            Object.entries(newInitial).forEach(([field, value]) => {
                formRef.current.getFormikBag().setFieldValue(field, value);
            });
        }
    }, [provider]);

    React.useEffect(() => {
        // On load we want to fetch the infrastructure provider and set it as the provider
        if (pipe.infrastructure_provider) {
            getInfrastructureProvider(pipe.infrastructure_provider.uuid).then((res) => {
                setProvider(res);
            });
        }
    }, []);

    const handleValuesChange = (values) => {
        const { infrastructure_provider: infrastructureProviderUuid, project } = values;

        if (infrastructureProviderUuid && infrastructureProviderUuid !== provider.uuid) {
            getInfrastructureProvider(infrastructureProviderUuid).then((res) => {
                setProvider(res);
            });
        }

        if (project && project !== lastSelectedProject) {
            // get the possible stages based on the project, set them as project_stage field choices and remove it
            // from excluded fields
            setLastSelectedProject(project);
            getStageChoices(project).then((projectStageChoices) => {
                if (projectStageChoices) {
                    removeProjectStageFromExcludedFields();
                    // setStageChoices(projectStageChoices);
                    const newExtraSchema = [...extraSchema];
                    newExtraSchema.push({
                        'fields.project_stage.ui.choices': projectStageChoices,
                    });
                    setExtraSchema(newExtraSchema);
                }
            });
        } else if (!project && !excludeFields.includes('project_stage')) {
            // if there is no project selected and project_stage is not in the excluded fields set it to null
            // and add it to excluded fields. Handles case when project and stage have been selected
            // but then the project field has been reset
            formRef.current.getFormikBag().setFieldValue('project_stage', null);
            const excludedFields = [...excludeFields];
            excludedFields.push('project_stage');
            setExcludeFields(excludedFields);
            setLastSelectedProject(null);
        }
    };

    const handleSave = (values) => {
        const newValues = { ...values };
        newValues.infrastructure_provider = provider;
        onSave(newValues);
    };

    const handleSubmit = (values) => values;

    return (
        <>
            <ApiFormWithModel
                forwardRef={formRef}
                objectType="pipe"
                resourceName="pipe.geo"
                resourceArguments={[mapUuid]}
                objectId={pipe.identity}
                initial={initial}
                excludeFields={excludeFields}
                onSave={handleSave}
                onClose={onClose}
                onSubmit={handleSubmit}
                height="100%"
                partialUpdate
                onValuesChanged={handleValuesChange}
                enableReinitialize
                extraSchema={extraSchema}
            />
        </>
    );
};

const apiCallback = (apiClient: ApiClient) => ({
    getInfrastructureProvider: {
        send: (uuid: UUID) => apiClient.resources.infrastructureProvider.get(uuid),
    },
    getProjectStages: {
        send: (uuid: UUID) => apiClient.resources.projectsStageNested.list(uuid),
    },
});

export default connectAPI(apiCallback)(
    connect(null, (dispatch) => (bindActionCreators(sidePanelActionCreators, dispatch)))(
        withRouter(
            injectIntl(PipeEditor),
        ),
    ),
);
